import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faSpinner, faTimes, faUpload } from '@fortawesome/free-solid-svg-icons';
import { Spinner } from '@material-tailwind/react';
import { rootUrl } from '../../../config/config';

const fallbackRequiredColumns = [
    'Cashbook Account ID',
    'Entry Type',
    'Entry Date',
    'Amount',
    'Payment Reference',
];

const formatLabel = (value) => String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getJobCount = (job, keys) => {
    for (const key of keys) {
        const value = job?.[key];
        if (value !== undefined && value !== null) {
            return value;
        }
    }
    return 0;
};

const normalizeColumns = (meta) => {
    const rawColumns = Array.isArray(meta)
        ? meta
        : meta?.columns || meta?.data?.columns || meta?.template_columns || [];

    if (!Array.isArray(rawColumns) || rawColumns.length === 0) {
        return [];
    }

    return rawColumns.map((column) => ({
        header: column.header || column.key || '',
        label: column.label || formatLabel(column.header || column.key),
        required: Boolean(column.required),
        description: column.description || '',
        example: column.example || '',
        allowedValues: Array.isArray(column.allowedValues) ? column.allowedValues : []
    }));
};

const CashbookImportModal = ({ isOpen, onClose, onCompleted, authHeaders }) => {
    const fileInputRef = useRef(null);
    const completionNotifiedRef = useRef(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [job, setJob] = useState(null);
    const [error, setError] = useState('');
    const [metaLoading, setMetaLoading] = useState(false);
    const [metaError, setMetaError] = useState('');
    const [templateMeta, setTemplateMeta] = useState(null);
    const [templateDownloading, setTemplateDownloading] = useState(false);

    const resetLocalState = useCallback(() => {
        completionNotifiedRef.current = false;
        setSelectedFile(null);
        setUploading(false);
        setPolling(false);
        setJob(null);
        setError('');
        setMetaError('');
        setTemplateMeta(null);
        setTemplateDownloading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    useEffect(() => {
        completionNotifiedRef.current = false;
        if (!isOpen) {
            resetLocalState();
            return undefined;
        }

        let cancelled = false;

        const fetchTemplateMeta = async () => {
            setMetaLoading(true);
            setMetaError('');
            try {
                const response = await axios.get(`${rootUrl}/api/cashbook/import-template/meta`, {
                    headers: authHeaders
                });
                if (!cancelled) {
                    setTemplateMeta(response.data?.data || response.data || null);
                }
            } catch (fetchError) {
                if (!cancelled) {
                    setMetaError(fetchError.response?.data?.message || 'Failed to load template guidance.');
                }
            } finally {
                if (!cancelled) {
                    setMetaLoading(false);
                }
            }
        };

        fetchTemplateMeta();

        return () => {
            cancelled = true;
        };
    }, [authHeaders, isOpen, resetLocalState]);

    const columns = useMemo(() => {
        const normalized = normalizeColumns(templateMeta);
        if (normalized.length === 0) {
            return fallbackRequiredColumns.map((header) => ({
                header,
                label: formatLabel(header),
                required: fallbackRequiredColumns.includes(header),
                description: '',
                allowedValues: []
            }));
        }

        return normalized;
    }, [templateMeta]);

    const requiredColumns = useMemo(() => {
        const explicitRequired = templateMeta?.required_columns || templateMeta?.data?.required_columns;
        if (Array.isArray(explicitRequired) && explicitRequired.length > 0) {
            return explicitRequired;
        }

        const required = columns.filter((column) => column.required).map((column) => column.header);
        return required.length > 0 ? required : fallbackRequiredColumns;
    }, [columns, templateMeta]);

    const optionalColumns = useMemo(() => {
        const explicitOptional = templateMeta?.optional_columns || templateMeta?.data?.optional_columns;
        if (Array.isArray(explicitOptional) && explicitOptional.length > 0) {
            return explicitOptional;
        }

        return columns.filter((column) => !column.required).map((column) => column.header);
    }, [columns, templateMeta]);

    const downloadTemplate = useCallback(async () => {
        setTemplateDownloading(true);
        setError('');
        try {
            const response = await axios.get(`${rootUrl}/api/cashbook/import-template`, {
                headers: authHeaders,
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'cashbook-import-template.csv';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (downloadError) {
            setError(downloadError.response?.data?.message || 'Failed to download template.');
        } finally {
            setTemplateDownloading(false);
        }
    }, [authHeaders]);

    const handleFileChange = useCallback((event) => {
        const file = event.target.files?.[0] || null;
        setSelectedFile(file);
        setError('');
    }, []);

    const uploadFile = useCallback(async () => {
        if (!selectedFile) {
            setError('Choose a CSV file before uploading.');
            return;
        }

        setUploading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const response = await axios.post(`${rootUrl}/api/cashbook/import-file`, formData, {
                headers: {
                    ...authHeaders
                }
            });

            const jobData = response.data?.data || response.data?.job || response.data || null;
            setJob(jobData);
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (uploadError) {
            setError(uploadError.response?.data?.message || 'Failed to upload import file.');
        } finally {
            setUploading(false);
        }
    }, [authHeaders, selectedFile]);

    useEffect(() => {
        if (!isOpen || !job?.import_id) {
            setPolling(false);
            return undefined;
        }

        const status = String(job.status || '').toLowerCase();
        if (status === 'completed' || status === 'failed') {
            setPolling(false);
            if (status === 'completed' && !completionNotifiedRef.current) {
                completionNotifiedRef.current = true;
                onCompleted?.(job);
            }
            return undefined;
        }

        let cancelled = false;
        setPolling(true);

        const timer = setInterval(async () => {
            try {
                const response = await axios.get(`${rootUrl}/api/cashbook/imports/${job.import_id}`, {
                    headers: authHeaders
                });
                const jobData = response.data?.data || response.data || null;
                if (cancelled || !jobData) {
                    return;
                }

                setJob(jobData);
                const nextStatus = String(jobData.status || '').toLowerCase();
                if (nextStatus === 'completed') {
                    setPolling(false);
                } else if (nextStatus === 'failed') {
                    setPolling(false);
                    setError(jobData.error_message || 'Import processing failed.');
                }
            } catch (pollError) {
                if (!cancelled) {
                    setPolling(false);
                    setError(pollError.response?.data?.message || 'Failed to check import status.');
                }
            }
        }, 3000);

        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [authHeaders, isOpen, job, onCompleted]);

    if (!isOpen) {
        return null;
    }

    const jobStatus = String(job?.status || '').toLowerCase();
    const isTerminal = jobStatus === 'completed' || jobStatus === 'failed';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-100">
                <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
                    <div>
                        <h3 className="text-xl font-bold text-slate-800">Import Cashbook CSV</h3>
                        <p className="mt-1 text-sm text-slate-500">
                            Upload a CSV file to create or update cashbook entries.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                        aria-label="Close import modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="max-h-[75vh] overflow-y-auto px-6 py-5 space-y-5">
                    <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-700">
                        Use the template to match expected headers and values. Existing records are updated where the backend finds matches, and new rows are inserted.
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={downloadTemplate}
                            disabled={templateDownloading}
                            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                            <FontAwesomeIcon icon={templateDownloading ? faSpinner : faFileDownload} spin={templateDownloading} />
                            {templateDownloading ? 'Downloading...' : 'Download Template'}
                        </button>
                        <button
                            onClick={uploadFile}
                            disabled={uploading || !selectedFile}
                            className="inline-flex items-center gap-2 rounded-md bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white hover:bg-[#e74a2e] disabled:opacity-50"
                        >
                            <FontAwesomeIcon icon={uploading ? faSpinner : faUpload} spin={uploading} />
                            {uploading ? 'Uploading...' : 'Upload CSV'}
                        </button>
                        {metaLoading && (
                            <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                                <Spinner className="h-4 w-4" /> Loading template guidance...
                            </span>
                        )}
                    </div>

                    {error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {metaError && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                            {metaError}
                        </div>
                    )}

                    <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center hover:border-[#FF5733]">
                        <FontAwesomeIcon icon={faUpload} className="text-2xl text-slate-400" />
                        <span className="text-sm font-semibold text-slate-700">
                            {selectedFile ? selectedFile.name : 'Choose a CSV file'}
                        </span>
                        <span className="text-xs text-slate-500">CSV files only</span>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </label>

                    {job && (
                        <div className={`rounded-2xl border px-4 py-4 text-sm ${
                            jobStatus === 'failed'
                                ? 'border-red-200 bg-red-50 text-red-700'
                                : jobStatus === 'completed'
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                    : 'border-slate-200 bg-slate-50 text-slate-700'
                        }`}>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="font-semibold capitalize">{jobStatus || 'queued'}</p>
                                {polling && !isTerminal && (
                                    <span className="inline-flex items-center gap-2 text-slate-500">
                                        <Spinner className="h-4 w-4" /> Processing in the background...
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                <p>Created: {getJobCount(job, ['created_count', 'inserted_count', 'created'])}</p>
                                <p>Updated: {getJobCount(job, ['updated_count', 'updated'])}</p>
                                <p>Skipped: {getJobCount(job, ['skipped_count', 'skipped'])}</p>
                                <p>Failed rows: {getJobCount(job, ['failed_count', 'failed'])}</p>
                            </div>
                            {job.error_message && (
                                <p className="mt-2 text-xs">{job.error_message}</p>
                            )}
                        </div>
                    )}

                    <div className="grid gap-4 lg:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <h4 className="text-sm font-bold text-slate-800">Required Columns</h4>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {requiredColumns.map((column) => (
                                    <span key={column} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                                        {column}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <h4 className="text-sm font-bold text-slate-800">Optional Columns</h4>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {optionalColumns.length > 0 ? optionalColumns.map((column) => (
                                    <span key={column} className="rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                                        {column}
                                    </span>
                                )) : (
                                    <span className="text-xs text-slate-500">No optional columns reported by the server.</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <h4 className="text-sm font-bold text-slate-800">Template Guidance</h4>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                            {columns.map((column) => (
                                <div key={column.header} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="text-sm font-semibold text-slate-800">{column.header}</p>
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${column.required ? 'bg-orange-100 text-orange-700' : 'bg-slate-200 text-slate-600'}`}>
                                            {column.required ? 'Required' : 'Optional'}
                                        </span>
                                    </div>
                                    {column.description && (
                                        <p className="mt-2 text-xs text-slate-500">{column.description}</p>
                                    )}
                                    {column.example && (
                                        <p className="mt-2 text-[11px] text-slate-400">
                                            Example: <span className="font-mono text-slate-500">{column.example}</span>
                                        </p>
                                    )}
                                    {column.allowedValues.length > 0 && (
                                        <p className="mt-2 text-[11px] text-slate-400">
                                            Allowed: <span className="font-mono text-slate-500">{column.allowedValues.join(', ')}</span>
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
                    <button
                        onClick={onClose}
                        className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                    >
                        {isTerminal ? 'Close' : 'Cancel'}
                    </button>
                    {jobStatus === 'failed' && (
                        <button
                            onClick={() => {
                                completionNotifiedRef.current = false;
                                setJob(null);
                                setError('');
                            }}
                            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                        >
                            Reset
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CashbookImportModal;
